import { useCallback, useEffect, useState } from "react";
import { initTeams, getMeetingHostInfo } from "./teams/teamsClient";
import {
  getGraphTokenSilent,
  getTeamsSsoToken,
  signInInteractive,
} from "./auth/authService";
import {
  getMyProfile,
  getUpcomingMeetings,
  type MeetingEvent,
  type UserProfile,
} from "./services/graphService";
import {
  getStoredSession,
  loginWithMicrosoft,
  loginWithTeamsSso,
  getMeetingReports,
  getBillingEntitlement,
  getMe,
  isAdminRole,
  type LiteSession,
  type MeetingReport,
  type MarketplaceEntitlement,
} from "./services/apiService";
import { WelcomeHeader } from "./components/WelcomeHeader";
import { MeetingsList } from "./components/MeetingsList";
import { ReportsList } from "./components/ReportsList";
import { ReportView } from "./components/ReportView";
import { UpgradeCta } from "./components/UpgradeCta";
import { SignInCard } from "./components/SignInCard";
import { Loading } from "./components/Loading";
import { ConfigPage } from "./components/ConfigPage";
import { MeetingPanel } from "./components/MeetingPanel";
import { SubscriptionGate } from "./components/SubscriptionGate";
import { SettingsPage } from "./components/SettingsPage";
import { UsersPage } from "./components/UsersPage";
import { AppNav } from "./components/AppNav";
import { GuidePage } from "./components/GuidePage";
import { ContactPage } from "./components/ContactPage";
import { OnboardingTour, hasSeenOnboarding } from "./components/OnboardingTour";
import { Footer } from "./components/ui";
import { useLanguage } from "./i18n/LanguageContext";

type Status = "initializing" | "signed-out" | "loading-data" | "ready";

/** Very small hash router for the Teams Lite app surfaces. */
function getRoute(): "config" | "meeting" | "settings" | "users" | "guide" | "contact" | "home" {
  const hash = window.location.hash.replace(/^#\/?/, "");
  if (hash.startsWith("config")) return "config";
  if (hash.startsWith("meeting")) return "meeting";
  if (hash.startsWith("settings")) return "settings";
  if (hash.startsWith("users")) return "users";
  if (hash.startsWith("guide")) return "guide";
  if (hash.startsWith("contact")) return "contact";
  return "home";
}

export function App() {
  const { t } = useLanguage();
  const [route, setRoute] = useState(getRoute());
  const [status, setStatus] = useState<Status>("initializing");
  const [insideTeams, setInsideTeams] = useState(false);
  const [inMeeting, setInMeeting] = useState(route === "meeting");
  const [meetingChatId, setMeetingChatId] = useState<string | null>(null);
  const [session, setSession] = useState<LiteSession | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [meetings, setMeetings] = useState<MeetingEvent[]>([]);
  const [reports, setReports] = useState<MeetingReport[]>([]);
  const [openReport, setOpenReport] = useState<MeetingReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [entitlement, setEntitlement] = useState<MarketplaceEntitlement | null>(null);
  const [myRole, setMyRole] = useState<number | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);

  /** Loads all home data from an authenticated Meeting Angelo session. */
  const loadData = useCallback(async (activeSession: LiteSession) => {
    setError(null);
    setStatus("loading-data");
    setSession(activeSession);

    // Marketplace subscription entitlement — gates the whole paid app.
    const entitlementResult = await getBillingEntitlement(activeSession.jwt).catch(() => null);
    setEntitlement(entitlementResult);

    // Real meeting reports from the Meeting Angelo database.
    const reportsPromise = getMeetingReports(activeSession.jwt).catch(() => []);

    // Profile + upcoming meetings straight from Microsoft Graph (when we have
    // a delegated Graph token; SSO login always returns one).
    let me: UserProfile | null = null;
    let upcoming: MeetingEvent[] = [];
    if (activeSession.graphToken) {
      [me, upcoming] = await Promise.all([
        getMyProfile(activeSession.graphToken).catch(() => null),
        getUpcomingMeetings(activeSession.graphToken).catch(() => []),
      ]);
    }
    if (!me) {
      const { firstName, lastName, email } = activeSession.user;
      me = {
        displayName: [firstName, lastName].filter(Boolean).join(" ") || email,
        mail: email || null,
        jobTitle: null,
      };
    }

    setProfile(me);
    setMeetings(upcoming);
    setReports(await reportsPromise);
    setStatus("ready");

    // Role is always read fresh from the backend (never cached in the
    // session) — it drives which admin sections/pages are shown.
    getMe(activeSession.jwt)
      .then((me2) => setMyRole(me2.role))
      .catch(() => setMyRole(null));

    if (!hasSeenOnboarding(activeSession.user.id)) {
      setShowOnboarding(true);
    }
  }, []);

  /**
   * Silent sign-in chain, in order of preference:
   *  1. Cached Meeting Angelo session (instant).
   *  2. Inside Teams: true SSO via authentication.getAuthToken().
   *  3. MSAL silent Graph token -> microsoft-login.
   * Falls back to the sign-in card when every silent path fails.
   */
  const signInSilent = useCallback(
    async (hostedInTeams: boolean): Promise<LiteSession | null> => {
      const cached = getStoredSession();
      if (cached) return cached;

      if (hostedInTeams) {
        try {
          const ssoToken = await getTeamsSsoToken();
          return await loginWithTeamsSso(ssoToken);
        } catch {
          // SSO unavailable or consent required: fall through to MSAL silent.
        }
      }

      try {
        const graphToken = await getGraphTokenSilent();
        if (graphToken) {
          return await loginWithMicrosoft(graphToken);
        }
      } catch {
        // Silent path failed: user interaction required.
      }
      return null;
    },
    [],
  );

  /** Interactive sign-in triggered from the SignInCard button. */
  const signIn = useCallback(async () => {
    setError(null);
    setStatus("loading-data");
    try {
      const graphToken = await signInInteractive(insideTeams);
      const newSession = await loginWithMicrosoft(graphToken);
      await loadData(newSession);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("signIn.errorFallback"));
      setStatus("signed-out");
    }
  }, [insideTeams, loadData]);

  useEffect(() => {
    if (route === "config") return;
    let cancelled = false;
    (async () => {
      const hostedInTeams = await initTeams();
      if (cancelled) return;
      setInsideTeams(hostedInTeams);
      if (hostedInTeams) {
        const hostInfo = await getMeetingHostInfo();
        if (cancelled) return;
        setInMeeting((prev) => prev || hostInfo.inMeeting);
        setMeetingChatId(hostInfo.meetingChatId);
      }
      try {
        const silentSession = await signInSilent(hostedInTeams);
        if (cancelled) return;
        if (silentSession) {
          await loadData(silentSession);
        } else {
          setStatus("signed-out");
        }
      } catch {
        if (!cancelled) setStatus("signed-out");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadData, route, signInSilent]);

  useEffect(() => {
    const onHashChange = () => setRoute(getRoute());
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  if (route === "config") {
    return <ConfigPage />;
  }

  if (status === "initializing") {
    return <Loading label={t("loading.starting")} />;
  }

  if (status === "signed-out") {
    return <SignInCard onSignIn={signIn} error={error} />;
  }

  if (status === "loading-data") {
    return <Loading label={t("loading.loadingMeetings")} />;
  }

  const refreshEntitlement = async () => {
    if (!session) return;
    const result = await getBillingEntitlement(session.jwt).catch(() => null);
    if (result) setEntitlement(result);
  };

  if (showOnboarding && session && route === "home") {
    return (
      <OnboardingTour
        userId={session.user.id}
        isAdmin={isAdminRole(myRole)}
        onFinish={() => setShowOnboarding(false)}
      />
    );
  }

  if (route === "settings" && session) {
    return (
      <>
        <AppNav route="settings" showUsers={isAdminRole(myRole)} />
        <SettingsPage
          session={session}
          entitlement={entitlement}
          reports={reports}
          insideTeams={insideTeams}
          myRole={myRole}
          onRefreshEntitlement={refreshEntitlement}
        />
      </>
    );
  }

  if (route === "users" && session) {
    if (!isAdminRole(myRole)) {
      window.location.hash = "";
      return <Loading label={t("loading.redirecting")} />;
    }
    return (
      <>
        <AppNav route="users" showUsers />
        <UsersPage session={session} myRole={myRole ?? 3} />
      </>
    );
  }

  if (route === "guide" && session) {
    return (
      <>
        <AppNav route="guide" showUsers={isAdminRole(myRole)} />
        <GuidePage isAdmin={isAdminRole(myRole)} />
      </>
    );
  }

  if (route === "contact" && session) {
    return <><AppNav route="contact" showUsers={isAdminRole(myRole)} /><ContactPage session={session} /></>;
  }

  if (entitlement && entitlement.configured && !entitlement.hasActiveSubscription) {
    return <SubscriptionGate entitlement={entitlement} insideTeams={insideTeams} onRefresh={refreshEntitlement} />;
  }

  if (inMeeting) {
    return (
      <MeetingPanel
        meetings={meetings}
        meetingChatId={meetingChatId}
        reports={reports}
        jwt={session?.jwt ?? null}
        insideTeams={insideTeams}
      />
    );
  }

  if (openReport && session) {
    return (
      <ReportView jwt={session.jwt} report={openReport} onBack={() => setOpenReport(null)} />
    );
  }

  return (
    <main className="ma-page">
      <AppNav route="home" showUsers={isAdminRole(myRole)} />
      <WelcomeHeader profile={profile} />
      <ReportsList reports={reports} loading={false} onOpen={setOpenReport} />
      <MeetingsList meetings={meetings} />
      <UpgradeCta insideTeams={insideTeams} />
      <button
        type="button"
        onClick={() => { window.location.hash = "#/guide"; }}
        style={{
          display: "block",
          margin: "14px auto 0",
          background: "none",
          border: "none",
          color: "var(--colorNeutralForeground3)",
          fontSize: "12px",
          cursor: "pointer",
          textDecoration: "underline",
        }}
      >
        {t("howItWorksLink")}
      </button>
      <Footer>{t("homeFooter")}</Footer>
    </main>
  );
}
